import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, PlatformAccount } from '../lib/api'
import {
  Check,
  X,
  Link,
  AlertTriangle
} from 'lucide-react'

const platformConfigs = [
  {
    id: 'twitter',
    name: 'Twitter / X',
    icon: '𝕏',
    description: 'Post tweets, threads, and engage with your audience',
    color: 'bg-black',
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: '💼',
    description: 'Share professional content and articles',
    color: 'bg-blue-700',
  },
  {
    id: 'reddit',
    name: 'Reddit',
    icon: '🔴',
    description: 'Post to subreddits and engage in discussions',
    color: 'bg-orange-600',
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    icon: '🎬',
    description: 'Generate video scripts and manage content',
    color: 'bg-pink-600',
  },
]

export function Platforms() {
  const queryClient = useQueryClient()

  const { data: platforms, isLoading } = useQuery({
    queryKey: ['platforms'],
    queryFn: () => api.getPlatforms(),
  })

  const { data: features } = useQuery({
    queryKey: ['features'],
    queryFn: () => api.getFeatures(),
  })

  const connectMutation = useMutation({
    mutationFn: (type: string) => api.connectPlatform(type),
    onSuccess: (data) => {
      // Redirect to OAuth URL
      if (data.authUrl) {
        window.location.href = data.authUrl
      }
    },
  })

  const disconnectMutation = useMutation({
    mutationFn: (id: string) => api.disconnectPlatform(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platforms'] })
    },
  })

  const getAccountForPlatform = (platformId: string): PlatformAccount | undefined => {
    return platforms?.find(p => p.platform === platformId)
  }

  const isPlatformConfigured = (platformId: string): boolean => {
    if (!features?.features.platforms) return false
    return features.features.platforms[platformId as keyof typeof features.features.platforms] || false
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Connected Platforms</h2>
        <p className="text-sm text-slate-500">Manage your social media connections</p>
      </div>

      {/* Platform Cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card h-40 animate-pulse bg-slate-100" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {platformConfigs.map((platform) => {
            const account = getAccountForPlatform(platform.id)
            const isConfigured = isPlatformConfigured(platform.id)

            return (
              <div key={platform.id} className="card">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 ${platform.color} rounded-xl flex items-center justify-center text-white text-2xl`}>
                    {platform.icon}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-900">{platform.name}</h3>
                      {account?.status === 'connected' && (
                        <span className="badge-success flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          Connected
                        </span>
                      )}
                      {account?.status === 'error' && (
                        <span className="badge-error flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Error
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-slate-500 mb-3">{platform.description}</p>

                    {account?.status === 'connected' ? (
                      <div className="flex items-center justify-between">
                        <div className="text-sm">
                          <p className="text-slate-700 font-medium">{account.username}</p>
                          <p className="text-slate-500 text-xs">
                            Connected {account.connectedAt && new Date(account.connectedAt).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={() => disconnectMutation.mutate(account.id)}
                          disabled={disconnectMutation.isPending}
                          className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                        >
                          <X className="w-4 h-4" />
                          Disconnect
                        </button>
                      </div>
                    ) : isConfigured ? (
                      <button
                        onClick={() => connectMutation.mutate(platform.id)}
                        disabled={connectMutation.isPending}
                        className="btn-primary text-sm flex items-center gap-2"
                      >
                        <Link className="w-4 h-4" />
                        Connect
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <AlertTriangle className="w-4 h-4 text-yellow-500" />
                        API credentials not configured
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* AI Services Status */}
      <div className="card">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">AI Services</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <AIServiceCard
            name="Claude (Anthropic)"
            description="Long-form, thoughtful content"
            enabled={features?.features.ai.anthropic}
          />
          <AIServiceCard
            name="GPT (OpenAI)"
            description="Social media, creative content"
            enabled={features?.features.ai.openai}
          />
          <AIServiceCard
            name="Gemini (Google)"
            description="Multimedia, technical content"
            enabled={features?.features.ai.gemini}
          />
        </div>
      </div>

      {/* SEO Services Status */}
      <div className="card">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">SEO Intelligence</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SEOServiceCard
            name="Google Search Console"
            enabled={features?.features.seo.googleSearchConsole}
          />
          <SEOServiceCard
            name="Google Analytics 4"
            enabled={features?.features.seo.googleAnalytics}
          />
          <SEOServiceCard
            name="Bing Webmaster"
            enabled={features?.features.seo.bingWebmaster}
          />
        </div>
      </div>
    </div>
  )
}

function AIServiceCard({
  name,
  description,
  enabled
}: {
  name: string
  description: string
  enabled?: boolean
}) {
  return (
    <div className={`p-4 rounded-lg border ${
      enabled
        ? 'border-green-200 bg-green-50'
        : 'border-slate-200 bg-slate-50'
    }`}>
      <div className="flex items-center gap-2 mb-1">
        {enabled ? (
          <Check className="w-4 h-4 text-green-600" />
        ) : (
          <X className="w-4 h-4 text-slate-400" />
        )}
        <span className={`font-medium ${enabled ? 'text-green-900' : 'text-slate-500'}`}>
          {name}
        </span>
      </div>
      <p className="text-sm text-slate-500">{description}</p>
    </div>
  )
}

function SEOServiceCard({
  name,
  enabled
}: {
  name: string
  enabled?: boolean
}) {
  return (
    <div className={`p-4 rounded-lg border flex items-center gap-3 ${
      enabled
        ? 'border-green-200 bg-green-50'
        : 'border-slate-200 bg-slate-50'
    }`}>
      {enabled ? (
        <Check className="w-5 h-5 text-green-600" />
      ) : (
        <X className="w-5 h-5 text-slate-400" />
      )}
      <span className={enabled ? 'text-green-900' : 'text-slate-500'}>
        {name}
      </span>
    </div>
  )
}
